import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import {
  Upload,
  FileSpreadsheet,
  Image,
  Loader2,
  CheckCircle,
  AlertCircle,
  Download,
  X,
  Users,
  ArrowLeft
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { useNavigate } from "react-router-dom";

const TenantBulkImport = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("excel");
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState("");
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const res = await axios.get(`${API}/properties`, { headers });
        setProperties(res.data);
      } catch (error) {
        toast.error("Failed to load properties");
      }
    };
    fetchProperties();
  }, [token]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setResult(null);
    }
  };

  const handleImport = async () => {
    if (!file) {
      toast.error("Please select a file");
      return;
    }

    setImporting(true);
    setResult(null);

    const formData = new FormData();
    formData.append("file", file);
    if (selectedProperty) {
      formData.append("property_id", selectedProperty);
    }

    try {
      const endpoint = activeTab === "excel" 
        ? `${API}/tenants/bulk-import`
        : `${API}/tenants/import-from-image`;
      
      const res = await axios.post(endpoint, formData, {
        headers: { ...headers, "Content-Type": "multipart/form-data" }
      });
      
      setResult(res.data);
      
      if (res.data.imported > 0) {
        toast.success(`Successfully imported ${res.data.imported} tenants!`);
      } else {
        toast.warning("No tenants were imported");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = () => {
    const csvContent = "name,email,phone,property_address,unit_number\nJohn Doe,john@example.com,555-123-4567,123 Main St,1A\nJane Smith,jane@example.com,555-987-6543,123 Main St,2B";
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "tenant_import_template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="animate-fade-in" data-testid="tenant-bulk-import-page">
      <div className="page-header">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/tenants")} data-testid="back-to-tenants">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="page-title">Bulk Import Tenants</h1>
            <p className="text-slate-500 mt-1">Import multiple tenants from Excel, CSV, or images</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-6">
              <TabsTrigger value="excel" className="flex items-center gap-2">
                <FileSpreadsheet className="w-4 h-4" />
                Excel/CSV Import
              </TabsTrigger>
              <TabsTrigger value="image" className="flex items-center gap-2">
                <Image className="w-4 h-4" />
                Image Import (AI)
              </TabsTrigger>
            </TabsList>

            <TabsContent value="excel">
              <Card>
                <CardHeader>
                  <CardTitle>Import from Excel or CSV</CardTitle>
                  <CardDescription>
                    Upload a spreadsheet with tenant information
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                    <div>
                      <p className="font-medium text-sm">Need a template?</p>
                      <p className="text-xs text-slate-500">Download our CSV template with the correct columns</p>
                    </div>
                    <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="download-template">
                      <Download className="w-4 h-4 mr-2" />
                      Download Template
                    </Button>
                  </div>

                  <div className="space-y-2">
                    <Label>Assign to Property (Optional)</Label>
                    <Select value={selectedProperty || "auto"} onValueChange={(v) => setSelectedProperty(v === "auto" ? "" : v)}>
                      <SelectTrigger data-testid="property-select">
                        <SelectValue placeholder="Auto-detect from file or select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="auto">Auto-detect from file</SelectItem>
                        {properties.map((p) => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.address}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500">
                      If selected, all tenants will be assigned to this property
                    </p>
                  </div>

                  <div
                    className={`border-2 border-dashed rounded-xl p-8 text-center transition-all ${
                      file ? "border-green-400 bg-green-50" : "border-slate-300 hover:border-green-400 hover:bg-slate-50"
                    }`}
                  >
                    {file ? (
                      <div className="space-y-3">
                        <FileSpreadsheet className="w-12 h-12 text-green-600 mx-auto" />
                        <p className="font-medium">{file.name}</p>
                        <p className="text-sm text-slate-500">
                          {(file.size / 1024).toFixed(1)} KB
                        </p>
                        <Button variant="outline" size="sm" onClick={() => setFile(null)}>
                          <X className="w-4 h-4 mr-1" />
                          Remove
                        </Button>
                      </div>
                    ) : (
                      <label className="cursor-pointer block">
                        <Upload className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                        <p className="font-medium">Drop file here or click to upload</p>
                        <p className="text-sm text-slate-500 mt-1">Excel (.xlsx, .xls) or CSV files</p>
                        <input
                          type="file"
                          className="hidden"
                          accept=".xlsx,.xls,.csv"
                          onChange={handleFileChange}
                          data-testid="file-input-excel"
                        />
                      </label>
                    )}
                  </div>

                  <Button
                    onClick={handleImport}
                    disabled={!file || importing}
                    className="w-full bg-green-700 hover:bg-green-800"
                    data-testid="import-button"
                  >
                    {importing ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Importing...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        Import Tenants
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="image">
              <Card>
                <CardHeader>
                  <CardTitle>Import from Image (AI-Powered)</CardTitle>
                  <CardDescription>
                    Upload an image of a tenant list and our AI will extract the data
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm text-amber-800">
                      <strong>Note:</strong> AI extraction works best with clear, well-lit images of typed or printed text. 
                      Handwritten text may have lower accuracy.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label>Assign to Property *</Label>
                    <Select value={selectedProperty || "none"} onValueChange={(v) => setSelectedProperty(v === "none" ? "" : v)}>
                      <SelectTrigger data-testid="property-select-image">
                        <SelectValue placeholder="Select property" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none" disabled>Select property</SelectItem>
                        {properties.map((p) => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.address}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500">
                      Required for image import - all extracted tenants will be assigned here
                    </p>
                  </div>

                  <div
                    className={`border-2 border-dashed rounded-xl p-8 text-center transition-all ${
                      file ? "border-green-400 bg-green-50" : "border-slate-300 hover:border-green-400 hover:bg-slate-50"
                    }`}
                  >
                    {file ? (
                      <div className="space-y-3">
                        <Image className="w-12 h-12 text-green-600 mx-auto" />
                        <p className="font-medium">{file.name}</p>
                        <Button variant="outline" size="sm" onClick={() => setFile(null)}>
                          <X className="w-4 h-4 mr-1" />
                          Remove
                        </Button>
                      </div>
                    ) : (
                      <label className="cursor-pointer block">
                        <Image className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                        <p className="font-medium">Drop image here or click to upload</p>
                        <p className="text-sm text-slate-500 mt-1">PNG, JPEG, or WebP</p>
                        <input
                          type="file"
                          className="hidden"
                          accept="image/png,image/jpeg,image/jpg,image/webp"
                          onChange={handleFileChange}
                          data-testid="file-input-image"
                        />
                      </label>
                    )}
                  </div>

                  <Button
                    onClick={handleImport}
                    disabled={!file || !selectedProperty || importing}
                    className="w-full bg-green-700 hover:bg-green-800"
                    data-testid="import-button-image"
                  >
                    {importing ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Analyzing Image...
                      </>
                    ) : (
                      <>
                        <Image className="w-4 h-4 mr-2" />
                        Extract & Import
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        {/* Results Panel */}
        <div>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Import Results
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!result ? (
                <div className="text-center py-8 text-slate-500">
                  <Upload className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                  <p>Results will appear here after import</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-3">
                    <div className="text-center p-3 bg-slate-50 rounded-lg">
                      <p className="text-2xl font-bold text-slate-900">{result.total_rows}</p>
                      <p className="text-xs text-slate-500">Total Rows</p>
                    </div>
                    <div className="text-center p-3 bg-green-50 rounded-lg">
                      <p className="text-2xl font-bold text-green-700">{result.imported}</p>
                      <p className="text-xs text-green-600">Imported</p>
                    </div>
                    <div className="text-center p-3 bg-amber-50 rounded-lg">
                      <p className="text-2xl font-bold text-amber-700">{result.skipped}</p>
                      <p className="text-xs text-amber-600">Skipped</p>
                    </div>
                  </div>

                  {result.imported > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium flex items-center gap-2 text-green-700">
                        <CheckCircle className="w-4 h-4" />
                        Imported Tenants
                      </p>
                      <div className="max-h-40 overflow-y-auto space-y-1">
                        {result.tenants.map((t, i) => (
                          <div key={i} className="text-sm p-2 bg-green-50 rounded flex items-center gap-2">
                            <CheckCircle className="w-3 h-3 text-green-600" />
                            {t.name}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {result.errors.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium flex items-center gap-2 text-amber-700">
                        <AlertCircle className="w-4 h-4" />
                        Skipped Rows
                      </p>
                      <div className="max-h-40 overflow-y-auto space-y-1">
                        {result.errors.map((e, i) => (
                          <div key={i} className="text-xs p-2 bg-amber-50 rounded">
                            {e.row ? `Row ${e.row}: ` : ""}{e.error}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => navigate("/tenants")}
                    data-testid="view-tenants-button"
                  >
                    View All Tenants
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Expected Format Card */}
          <Card className="mt-4">
            <CardHeader>
              <CardTitle className="text-sm">Expected File Format</CardTitle>
            </CardHeader>
            <CardContent className="text-xs space-y-2">
              <div className="flex justify-between">
                <span className="text-slate-600">name</span>
                <Badge variant="secondary">Required</Badge>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">email</span>
                <Badge variant="outline">Optional</Badge>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">phone</span>
                <Badge variant="outline">Optional</Badge>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">property_address</span>
                <Badge variant="outline">Optional*</Badge>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">unit_number</span>
                <Badge variant="outline">Optional</Badge>
              </div>
              <p className="text-slate-400 pt-2 border-t">
                *If property not in file, select one above
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default TenantBulkImport;
